import React, { useCallback, useEffect, useMemo, useState } from "react";
import { get, post } from "../../services/api";
import MobileInput from "./MobileInput";
import styles from "./InviteDialog.module.css";
import { toast } from "react-hot-toast";

const IconClose = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none">
    <path
      d="M18 6L6 18M6 6l12 12"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const formatDate = (value) => {
  if (!value) return "No renewal date available";

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "No renewal date available";

  return new Intl.DateTimeFormat("en-ZA", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(parsed);
};

// Human-readable expiry/status line for a sent invite row.
const describeInviteStatus = (invite) => {
  const status = `${invite?.status || ""}`.toLowerCase();
  if (status === "accepted") {
    return { label: "Accepted", tone: "accepted" };
  }
  if (status === "revoked") {
    return { label: "Revoked", tone: "revoked" };
  }
  if (status === "expired" || invite?.expired) {
    return { label: "Expired", tone: "expired" };
  }
  // pending + still valid → show how long until it expires
  const expiresAt = invite?.expiresAt ? new Date(invite.expiresAt) : null;
  if (expiresAt && !Number.isNaN(expiresAt.getTime())) {
    const ms = expiresAt.getTime() - Date.now();
    const days = Math.ceil(ms / (1000 * 60 * 60 * 24));
    if (days <= 0) return { label: "Expires today", tone: "pending" };
    if (days === 1) return { label: "Expires in 1 day", tone: "pending" };
    return { label: `Expires in ${days} days`, tone: "pending" };
  }
  return { label: "Pending", tone: "pending" };
};

export default function InviteDialog({
  open,
  onClose,
  scope = "clan",
  subclanId = "",
  subclanName = "",
}) {
  const [tab, setTab] = useState("email");
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [inviteContext, setInviteContext] = useState(null);
  const [contextLoading, setContextLoading] = useState(false);
  const [contextError, setContextError] = useState("");
  const [checkoutSeats, setCheckoutSeats] = useState("1");
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  // Sent-invite tracking (req #2)
  const [invitations, setInvitations] = useState([]);
  const [invitesLoading, setInvitesLoading] = useState(false);
  const [invitesError, setInvitesError] = useState("");
  const [rowBusyId, setRowBusyId] = useState("");
  const isSubclanInvite = scope === "subclan" && Boolean(`${subclanId || ""}`.trim());
  const targetName = `${subclanName || ""}`.trim() || "this subclan";

  const loadInviteContext = useCallback(async () => {
    try {
      setContextLoading(true);
      setContextError("");
      const response = await get("/api/v1/users/invite/context");
      setInviteContext(response?.data || null);
    } catch (nextError) {
      setInviteContext(null);
      setContextError(
        nextError?.response?.data?.message ||
          nextError?.message ||
          "We couldn't load the clan invite details right now."
      );
    } finally {
      setContextLoading(false);
    }
  }, []);

  const loadInvitations = useCallback(async () => {
    try {
      setInvitesLoading(true);
      setInvitesError("");
      const response = await get(
        isSubclanInvite
          ? `/api/v1/subclans/${encodeURIComponent(subclanId)}/invite/list`
          : "/api/v1/users/invite/list"
      );
      const list = Array.isArray(response?.data?.invitations)
        ? response.data.invitations
        : [];
      setInvitations(list);
    } catch (nextError) {
      setInvitations([]);
      setInvitesError(
        nextError?.response?.data?.message ||
          nextError?.message ||
          "We couldn't load sent invites right now."
      );
    } finally {
      setInvitesLoading(false);
    }
  }, [isSubclanInvite, subclanId]);

  useEffect(() => {
    if (!open) return;

    setEmail("");
    setMobile("");
    setMessage("");
    setError("");
    setTab("email");
    setCheckoutSeats("1");
    if (isSubclanInvite) {
      setInviteContext(null);
      setContextError("");
    } else {
      loadInviteContext();
    }
    loadInvitations();
  }, [open, isSubclanInvite, loadInviteContext, loadInvitations]);

  const seatSummary = inviteContext?.seatSummary || null;
  const permissions = inviteContext?.permissions || {};
  const availableSeats = Math.max(0, Number(seatSummary?.availableSeats) || 0);
  const usedSeats = Math.max(0, Number(seatSummary?.usedSeats) || 0);
  const totalSeats = Math.max(0, Number(seatSummary?.totalSeats) || 0);
  const overCapacityBy = Math.max(0, Number(seatSummary?.overCapacityBy) || 0);
  const checkoutSeatCount = Math.max(1, Number.parseInt(checkoutSeats, 10) || 1);

  const inviteDisabled = useMemo(() => {
    if (loading) return true;
    if (isSubclanInvite) return false;
    if (contextLoading) return true;
    if (seatSummary && availableSeats <= 0) return true;
    return false;
  }, [availableSeats, contextLoading, isSubclanInvite, loading, seatSummary]);

  if (!open) return null;

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setMessage("");

    const payload = {};
    if (tab === "email") {
      if (!email.trim() || !email.includes("@")) {
        setError("Please enter a valid email address.");
        return;
      }
      payload.email = email.trim();
    } else {
      if (!mobile.trim()) {
        setError("Please enter a mobile number.");
        return;
      }
      payload.mobile = mobile.trim();
    }

    if (!isSubclanInvite && seatSummary && availableSeats <= 0) {
      setError("No seats are available right now. Add more seats before sending another invite.");
      return;
    }

    try {
      setLoading(true);
      const response = await post(
        isSubclanInvite
          ? `/api/v1/subclans/${encodeURIComponent(subclanId)}/invite`
          : "/api/v1/users/invite",
        payload
      );
      const nextMessage =
        response?.message || response?.data?.message || "Invitation sent successfully!";
      setMessage(nextMessage);
      toast.success(nextMessage);
      if (tab === "email") {
        setEmail("");
      } else {
        setMobile("");
      }
      // Refresh tracking + seat counts so the new invite shows immediately.
      loadInvitations();
      if (!isSubclanInvite) {
        loadInviteContext();
      }
    } catch (nextError) {
      setError(
        nextError?.response?.data?.message ||
          nextError?.message ||
          "Failed to send invitation."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async (invite) => {
    if (!invite?.id) return;
    try {
      setRowBusyId(invite.id);
      await post(
        isSubclanInvite
          ? `/api/v1/subclans/${encodeURIComponent(subclanId)}/invite/${encodeURIComponent(invite.id)}/resend`
          : `/api/v1/users/invite/${invite.id}/resend`,
        {}
      );
      toast.success(`Invite re-sent to ${invite.email || invite.mobile || "recipient"}.`);
      await loadInvitations();
    } catch (nextError) {
      toast.error(
        nextError?.response?.data?.message ||
          nextError?.message ||
          "Couldn't resend that invite."
      );
    } finally {
      setRowBusyId("");
    }
  };

  const handleRevoke = async (invite) => {
    if (!invite?.id) return;
    try {
      setRowBusyId(invite.id);
      await post(
        isSubclanInvite
          ? `/api/v1/subclans/${encodeURIComponent(subclanId)}/invite/${encodeURIComponent(invite.id)}/revoke`
          : `/api/v1/users/invite/${invite.id}/revoke`,
        {}
      );
      toast.success(`Invite to ${invite.email || invite.mobile || "recipient"} revoked.`);
      await loadInvitations();
    } catch (nextError) {
      toast.error(
        nextError?.response?.data?.message ||
          nextError?.message ||
          "Couldn't revoke that invite."
      );
    } finally {
      setRowBusyId("");
    }
  };

  const handleSeatCheckout = async () => {
    setError("");
    setMessage("");

    try {
      setCheckoutLoading(true);
      const response = await post("/api/v1/users/invite/seat-checkout", {
        additionalSeats: checkoutSeatCount,
      });

      if (response?.paymentRequired === false) {
        const nextMessage =
          response?.message || "Seats updated successfully for this clan.";
        setMessage(nextMessage);
        setInviteContext((current) =>
          current
            ? {
                ...current,
                seatSummary: response?.data?.seatSummary || current.seatSummary,
              }
            : current
        );
        toast.success(nextMessage);
        return;
      }

      const checkoutUrl = `${response?.data?.checkoutSessionUrl || ""}`.trim();
      if (!checkoutUrl) {
        throw new Error("We couldn't start seat checkout right now.");
      }

      const checkoutWindow = window.open(
        checkoutUrl,
        "_blank",
        "noopener,noreferrer"
      );

      if (!checkoutWindow) {
        window.location.assign(checkoutUrl);
      } else {
        toast.success(
          "Seat checkout opened in a new tab. Complete payment there, then come back here."
        );
      }
    } catch (nextError) {
      setError(
        nextError?.response?.data?.message ||
          nextError?.message ||
          "We couldn't open seat checkout right now."
      );
    } finally {
      setCheckoutLoading(false);
    }
  };

  const handleBackdropClick = (event) => {
    if (event.target.dataset.backdrop) {
      onClose();
    }
  };

  const canManageSeats = permissions?.canManageSeats !== false;

  return (
    <div
      className={styles.overlay}
      data-backdrop="true"
      onPointerDown={handleBackdropClick}
    >
      <form
        className={styles.dialog}
        onSubmit={handleSubmit}
        role="dialog"
        aria-modal="true"
        aria-labelledby="invite-title"
      >
        <div className={styles.header}>
          <div>
            <p className={styles.eyebrow}>
              {isSubclanInvite ? "Subclan invites" : "Clan invites"}
            </p>
            <h3 id="invite-title" className={styles.title}>
              {isSubclanInvite ? `Invite to ${targetName}` : "Invite members"}
            </h3>
          </div>
          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Close dialog"
          >
            <IconClose />
          </button>
        </div>

        <div className={styles.body}>
          {/* ───────────── SEATS (all seat management in one place) ───────────── */}
          {!isSubclanInvite ? (
          <section className={styles.summarySection}>
            <div className={styles.summaryHeader}>
              <div>
                <div className={styles.summaryTitle}>
                  {inviteContext?.tenant?.name || "Current clan"} · Seats
                </div>
                <p className={styles.summaryHint}>
                  Every member uses one seat. Manage your seat budget here.
                </p>
              </div>
              <button
                className={styles.refreshButton}
                type="button"
                onClick={loadInviteContext}
                disabled={contextLoading}
              >
                {contextLoading ? "Refreshing..." : "Refresh"}
              </button>
            </div>

            {contextError ? (
              <div className={styles.noticeError}>{contextError}</div>
            ) : (
              <div className={styles.metricGrid}>
                <div className={styles.metricCard}>
                  <span className={styles.metricLabel}>Available</span>
                  <strong className={styles.metricValue}>{availableSeats}</strong>
                </div>
                <div className={styles.metricCard}>
                  <span className={styles.metricLabel}>Used</span>
                  <strong className={styles.metricValue}>{usedSeats}</strong>
                </div>
                <div className={styles.metricCard}>
                  <span className={styles.metricLabel}>Total seats</span>
                  <strong className={styles.metricValue}>{totalSeats}</strong>
                </div>
              </div>
            )}

            {seatSummary ? (
              <div className={styles.planStrip}>
                <div>
                  <span className={styles.planLabel}>Current plan</span>
                  <strong className={styles.planValue}>{seatSummary.planName}</strong>
                </div>
                <div>
                  <span className={styles.planLabel}>Current period ends</span>
                  <strong className={styles.planValue}>
                    {formatDate(seatSummary.currentPeriodEnd)}
                  </strong>
                </div>
              </div>
            ) : null}

            {overCapacityBy > 0 ? (
              <div className={styles.noticeWarning}>
                This clan is currently over capacity by {overCapacityBy} seat
                {overCapacityBy !== 1 ? "s" : ""}. Add more seats before inviting
                anyone else.
              </div>
            ) : null}

            {/* Buy more seats — kept inside the Seats group */}
            <div className={styles.seatBuyRow}>
              <label htmlFor="invite-seat-count" className={styles.label}>
                Add seats
              </label>
              <div className={styles.checkoutRow}>
                <input
                  id="invite-seat-count"
                  type="number"
                  min="1"
                  step="1"
                  className={styles.seatInput}
                  value={checkoutSeats}
                  onChange={(event) => setCheckoutSeats(event.target.value)}
                  disabled={checkoutLoading || !canManageSeats}
                />
                <button
                  type="button"
                  className={styles.checkoutButton}
                  onClick={handleSeatCheckout}
                  disabled={checkoutLoading || !canManageSeats}
                >
                  {checkoutLoading ? "Opening..." : "Pay for more seats"}
                </button>
              </div>
              {!canManageSeats ? (
                <p className={styles.helperText}>
                  Only clan admins can add more seats for this workspace.
                </p>
              ) : (
                <p className={styles.helperText}>
                  Opens a secure checkout in a new tab and brings you back here once payment is done.
                </p>
              )}
            </div>
          </section>
          ) : null}

          {/* ───────────── SEND INVITE (compose + inline send) ───────────── */}
          <section className={styles.inviteSection}>
            <div className={styles.summaryTitle}>
              {isSubclanInvite ? `Send an invite to ${targetName}` : "Send an invite"}
            </div>
            <div className={styles.tabs}>
              <button
                type="button"
                className={`${styles.tab} ${tab === "email" ? styles.tabActive : ""}`}
                onClick={() => {
                  setTab("email");
                  setError("");
                  setMessage("");
                }}
              >
                Email
              </button>
              <button
                type="button"
                className={`${styles.tab} ${tab === "mobile" ? styles.tabActive : ""}`}
                onClick={() => {
                  setTab("mobile");
                  setError("");
                  setMessage("");
                }}
              >
                Mobile
              </button>
            </div>

            {message ? <div className={styles.noticeSuccess}>{message}</div> : null}
            {error ? <div className={styles.noticeError}>{error}</div> : null}

            {tab === "email" ? (
              <div>
                <label htmlFor="invite-email" className={styles.label}>
                  Email address
                </label>
                <input
                  id="invite-email"
                  type="email"
                  className={styles.input}
                  placeholder="name@example.com"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  disabled={loading}
                />
              </div>
            ) : (
              <div>
                <label htmlFor="invite-mobile" className={styles.label}>
                  Mobile number
                </label>
                <MobileInput
                  id="invite-mobile"
                  className={styles.mobileWrapper}
                  value={mobile}
                  onChange={setMobile}
                  disabled={loading}
                />
              </div>
            )}

            <button
              type="submit"
              className={styles.sendInlineButton}
              disabled={inviteDisabled}
            >
              {loading
                ? "Sending..."
                : !isSubclanInvite && availableSeats <= 0 && seatSummary
                ? "No seats available"
                : "Send invite"}
            </button>
            <p className={styles.helperText}>
              {isSubclanInvite
                ? `The recipient gets a unique link that adds them to ${targetName}. Protected subclan invites keep them inside the subclan only.`
                : "The recipient gets a unique link that only works for them and takes them straight into the clan."}
            </p>
          </section>

          {/* ───────────── SENT INVITES (tracking — req #2) ───────────── */}
          <section className={styles.trackingSection}>
            <div className={styles.summaryHeader}>
              <div className={styles.summaryTitle}>Sent invites</div>
              <button
                className={styles.refreshButton}
                type="button"
                onClick={loadInvitations}
                disabled={invitesLoading}
              >
                {invitesLoading ? "Loading..." : "Refresh"}
              </button>
            </div>

            {invitesError ? (
              <div className={styles.noticeError}>{invitesError}</div>
            ) : invitesLoading && invitations.length === 0 ? (
              <p className={styles.helperText}>Loading sent invites…</p>
            ) : invitations.length === 0 ? (
              <p className={styles.helperText}>
                No invites sent yet. Invites you send will appear here with their
                status and expiry.
              </p>
            ) : (
              <ul className={styles.inviteList}>
                {invitations.map((invite) => {
                  const status = describeInviteStatus(invite);
                  const recipient = invite.email || invite.mobile || "Unknown";
                  const busy = rowBusyId === invite.id;
                  const isOpen =
                    `${invite.status || ""}`.toLowerCase() === "pending" &&
                    !invite.expired;
                  const canResend =
                    `${invite.status || ""}`.toLowerCase() !== "accepted";
                  return (
                    <li key={invite.id} className={styles.inviteRow}>
                      <div className={styles.inviteRowMain}>
                        <span className={styles.inviteRecipient}>{recipient}</span>
                        <span className={styles.inviteMeta}>
                          {invite.type === "subclan" && invite.subclanName
                            ? `Subclan · ${invite.subclanName}`
                            : "Clan"}
                          {Array.isArray(invite.channels) && invite.channels.length
                            ? ` · ${invite.channels.join(", ")}`
                            : ""}
                        </span>
                      </div>
                      <span
                        className={`${styles.statusBadge} ${
                          styles[`status_${status.tone}`] || ""
                        }`}
                      >
                        {status.label}
                      </span>
                      <div className={styles.inviteActions}>
                        {canResend ? (
                          <button
                            type="button"
                            className={styles.rowButton}
                            onClick={() => handleResend(invite)}
                            disabled={busy}
                          >
                            {busy ? "…" : "Resend"}
                          </button>
                        ) : null}
                        {isOpen ? (
                          <button
                            type="button"
                            className={`${styles.rowButton} ${styles.rowButtonDanger}`}
                            onClick={() => handleRevoke(invite)}
                            disabled={busy}
                          >
                            {busy ? "…" : "Revoke"}
                          </button>
                        ) : null}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        </div>

        <div className={styles.footer}>
          <button
            type="button"
            className={styles.cancelButton}
            onClick={onClose}
            disabled={loading || checkoutLoading}
          >
            Close
          </button>
        </div>
      </form>
    </div>
  );
}
